/**
 * Reactive analytics-consent store — the single source of truth for whether the
 * three trackers (PostHog, Datadog RUM business events, GA4) may fire.
 *
 * Backed by the SAME localStorage key the existing CookieConsentBanner writes
 * (`mb_cookie_consent` = 'accepted' | 'declined' | null), so this layers on top
 * of the current banner without changing its contract. The difference from the
 * old check-once `getConsentStatus()` is that this is REACTIVE: callers can
 * subscribe and flip providers on the moment a visitor accepts, no reload.
 *
 * SSR-safe: every accessor guards `typeof window`.
 */

const CONSENT_KEY = 'mb_cookie_consent';
const CONSENT_EVENT = 'mb:analytics-consent-changed';

export type AnalyticsConsent = 'accepted' | 'declined' | null;

/** Current consent decision, or null if the visitor hasn't chosen yet. */
export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === 'undefined') return null;
  try {
    const val = window.localStorage.getItem(CONSENT_KEY);
    return val === 'accepted' || val === 'declined' ? val : null;
  } catch {
    return null;
  }
}

/** True only when the visitor has explicitly accepted. */
export function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsent() === 'accepted';
}

/**
 * Persist a consent decision and notify all subscribers in the same tab.
 * The CookieConsentBanner can call this so the new store stays in sync; it also
 * writes the legacy key shape so `getConsentStatus()` keeps working.
 */
export function setAnalyticsConsent(accepted: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_KEY, accepted ? 'accepted' : 'declined');
  } catch {
    // localStorage unavailable (private mode / blocked) — still notify in-memory
  }
  window.dispatchEvent(
    new CustomEvent<AnalyticsConsent>(CONSENT_EVENT, {
      detail: accepted ? 'accepted' : 'declined',
    }),
  );
}

/**
 * Subscribe to consent changes. Fires on same-tab updates (via setAnalyticsConsent)
 * and cross-tab updates (via the native `storage` event). Returns an unsubscribe fn.
 */
export function subscribeConsent(cb: (consent: AnalyticsConsent) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const onCustom = (e: Event) => cb((e as CustomEvent<AnalyticsConsent>).detail);
  const onStorage = (e: StorageEvent) => {
    if (e.key === CONSENT_KEY) cb(getAnalyticsConsent());
  };

  window.addEventListener(CONSENT_EVENT, onCustom);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(CONSENT_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
